"use client";

import bs58 from "bs58";
import { evmProvider, solanaProvider } from "./detect";
import { toQuantity, transferCallData } from "./erc20";
import { checksumAddress } from "./message";
import type { DetectedWallet, Eip1193Provider, WalletConnection } from "./types";
import { WalletError } from "./types";

const SOLANA_CHAIN_ID = 1;

const REJECTION_CODES = new Set([4001]);

const isRejection = (cause: unknown) => {
  if (typeof cause !== "object" || cause === null) {
    return false;
  }

  const code = (cause as { code?: unknown }).code;
  const message = (cause as { message?: unknown }).message;

  return (
    (typeof code === "number" && REJECTION_CODES.has(code)) ||
    (typeof code === "string" && code === "ACTION_REJECTED") ||
    (typeof message === "string" && /reject|denied|cancell?ed/i.test(message))
  );
};

const walletError = (cause: unknown, fallback: string) => {
  if (cause instanceof WalletError) {
    return cause;
  }

  if (isRejection(cause)) {
    return new WalletError("rejected", "The wallet request was rejected.");
  }

  return new WalletError("failed", cause instanceof Error && cause.message ? cause.message : fallback);
};

const toUtf8Hex = (value: string) => {
  let out = "0x";

  for (const byte of new TextEncoder().encode(value)) {
    out += byte.toString(16).padStart(2, "0");
  }

  return out;
};

const chainIdOf = async (provider: Eip1193Provider) => {
  const chainId = (await provider.request({ method: "eth_chainId" })) as string | number | null;

  return typeof chainId === "string" ? Number.parseInt(chainId, 16) : chainId;
};

const connectEvm = async (wallet: DetectedWallet): Promise<WalletConnection> => {
  const provider = evmProvider(wallet.id);
  const accounts = (await provider.request({ method: "eth_requestAccounts" })) as string[] | null;
  const account = accounts?.[0];

  if (!account) {
    throw new WalletError("failed", `${wallet.name} did not share an account.`);
  }

  return {
    address: checksumAddress(account),
    chainId: (await chainIdOf(provider)) ?? 1,
  };
};

const connectSolana = async (wallet: DetectedWallet): Promise<WalletConnection> => {
  const provider = solanaProvider(wallet.id);
  const connected = await provider.connect();
  const address = connected?.publicKey?.toString() ?? provider.publicKey?.toString();

  if (!address) {
    throw new WalletError("failed", `${wallet.name} did not share an account.`);
  }

  return { address, chainId: SOLANA_CHAIN_ID };
};

export const connectWallet = async (wallet: DetectedWallet) => {
  try {
    return wallet.chain === "evm" ? await connectEvm(wallet) : await connectSolana(wallet);
  } catch (cause) {
    throw walletError(cause, `Could not connect to ${wallet.name}.`);
  }
};

const signEvm = async (wallet: DetectedWallet, address: string, message: string) => {
  const signature = await evmProvider(wallet.id).request({
    method: "personal_sign",
    params: [toUtf8Hex(message), address],
  });

  if (typeof signature !== "string") {
    throw new WalletError("failed", `${wallet.name} returned no signature.`);
  }

  return signature;
};

const signSolana = async (wallet: DetectedWallet, message: string) => {
  const signed = await solanaProvider(wallet.id).signMessage(new TextEncoder().encode(message), "utf8");
  const bytes = signed instanceof Uint8Array ? signed : signed?.signature;

  if (!bytes) {
    throw new WalletError("failed", `${wallet.name} returned no signature.`);
  }

  return bs58.encode(bytes);
};

export const signWalletMessage = async (wallet: DetectedWallet, address: string, message: string) => {
  try {
    return wallet.chain === "evm"
      ? await signEvm(wallet, address, message)
      : await signSolana(wallet, message);
  } catch (cause) {
    throw walletError(cause, `${wallet.name} could not sign the message.`);
  }
};

/* A deposit is the player moving their own tokens to the address the platform
   issued for the route. Nothing here credits an account — that is
   `POST /deposit`, which needs the hash this returns. */

const UNKNOWN_CHAIN = 4902;

const switchEvmChain = async (wallet: DetectedWallet, chainId: number) => {
  const provider = evmProvider(wallet.id);

  if ((await chainIdOf(provider)) === chainId) {
    return;
  }

  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: toQuantity(chainId) }],
    });
  } catch (cause) {
    /* 4902 is the wallet saying it has never heard of this chain. Adding it
       needs an RPC URL and a currency name the API does not publish, so this
       is where the flow honestly stops rather than guessing at endpoints. */
    if (typeof cause === "object" && cause !== null && (cause as { code?: unknown }).code === UNKNOWN_CHAIN) {
      throw new WalletError(
        "failed",
        `${wallet.name} does not have this network configured. Add it in the wallet, then try again.`,
      );
    }

    throw cause;
  }

  if ((await chainIdOf(provider)) !== chainId) {
    throw new WalletError(
      "failed",
      `${wallet.name} is still on another network. Switch to it in the wallet, then try again.`,
    );
  }
};

export const sendTokenTransfer = async (
  wallet: DetectedWallet,
  transfer: { from: string; token: string; recipient: string; amount: bigint; chainId: number },
) => {
  if (wallet.chain !== "evm") {
    throw new WalletError("failed", `Paying from ${wallet.name} is not supported on this network.`);
  }

  try {
    await switchEvmChain(wallet, transfer.chainId);

    const hash = await evmProvider(wallet.id).request({
      method: "eth_sendTransaction",
      params: [
        {
          from: transfer.from,
          to: transfer.token,
          data: transferCallData(transfer.recipient, transfer.amount),
          chainId: toQuantity(transfer.chainId),
        },
      ],
    });

    if (typeof hash !== "string") {
      throw new WalletError("failed", `${wallet.name} returned no transaction hash.`);
    }

    return hash;
  } catch (cause) {
    throw walletError(cause, `${wallet.name} could not send the transfer.`);
  }
};
